import * as React from 'react';
import * as R from 'ramda';
import { Box, ButtonBase, Tooltip, Typography } from '@material-ui/core';
import { VariantType } from 'notistack';
import { CustomIcon } from '../../../custom-forms';
import { useStyles, cx } from '../../../custom-forms';
import { CaseCalculationAlternativePart, CaseCalculationPart } from '../../../interfaces/Case';
import { WorkflowCalculation } from '../../../interfaces/Workflow';
import { AdditionalInfoCode, SupplierCode } from '../../../helpers/Constants';
import { useAppSelector } from '../../../store/configureStore';
import { useTranslate } from '../../../hooks/useTranslate';
import { useVariantsEnabled } from '../context/CalculationFlagsContext';
import {
  alternativeCode,
  alternativeQuality,
  alternativeSupplierName,
  alternativeDifference,
  alternativeUnitPrice,
  DASH,
  displayAmount
} from '../utils/calculation.utils';

// Suppliers flag externally-stocked parts by appending this note to the delivery time.
const STOCK_EXTERN = 'in cazul in care piesa se afla in stocul extern';

// Value + info icon: the text ellipsizes within the cell and the icon stays glued to it; when even
// the shrunk text leaves no room, the icon wraps onto the next line instead of being clipped.
const infoValueStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  maxWidth: '100%',
  flexWrap: 'wrap'
};

export interface AlternativeCalculationCellsProps {
  part: CaseCalculationPart;
  alternativePart: CaseCalculationAlternativePart;
  workflowCalculation: WorkflowCalculation | null;
  onNotify: (message: string, variant: VariantType) => void;
}

const AlternativeCalculationCells: React.FC<AlternativeCalculationCellsProps> = ({
  part,
  alternativePart,
  workflowCalculation,
  onNotify
}) => {
  const c = useStyles();
  const t = useTranslate();
  const country = useAppSelector((s) => s.app?.appUser?.organization?.country ?? null);
  const showNetPrice = workflowCalculation?.alternativeNetPrice ?? false;
  const variantsEnabled = useVariantsEnabled();

  const signedAmount = (diff: number) =>
    (diff < 0 ? '−' : '+') + displayAmount(Math.abs(diff), country);
  const code = alternativeCode(alternativePart);
  const diff = alternativeDifference(part, alternativePart);
  const diffClass = diff < 0 ? c.successText : diff > 0 ? c.dangerText : c.muted;

  const copyCode = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (navigator.clipboard) navigator.clipboard.writeText(code);
    onNotify(t('ALT_CALC_CODE_COPIED', 'Cod copiat: ') + code, 'info');
  };

  const renderDeliveryTime = (value: string | null) => {
    const str = (value ?? '').toString();
    if (!str.includes(STOCK_EXTERN)) return str || DASH;
    return (
      <>
        <span>{str.split(STOCK_EXTERN)[0]}</span>
        <b className={c.dangerText}>{STOCK_EXTERN}</b>
      </>
    );
  };

  const qty = alternativePart.availableQuantity;
  const supplierIsLkq =
    alternativeSupplierName(alternativePart).toUpperCase() === SupplierCode.LKQ.toUpperCase();
  const isNonReturnable =
    !R.isNil(alternativePart.additionalInfo) &&
    alternativePart.additionalInfo !== '' &&
    alternativePart.additionalInfo === AdditionalInfoCode.PRODUS_NU_POATE_FI_RETURNAT;

  const locationCell = (
    <Typography variant="body2" component="div" className={cx(c.minW0, c.semibold)}>
      {alternativePart.stockLocation ? (
        <Tooltip
          title={
            <div>
              {t('ORDER_PART_DETAILS_LOCATION_LABEL', 'Locatie') +
                ': ' +
                alternativePart.stockLocation}
            </div>
          }
        >
          <span style={infoValueStyle}>
            <span className={c.ellipsis}>{alternativePart.stockLocation.split('\n')[0]}</span>
            <CustomIcon name="info" />
          </span>
        </Tooltip>
      ) : (
        alternativePart.stockLocation
      )}
      {isNonReturnable && (
        <p className={c.dangerText} style={{ margin: 0 }}>
          {alternativePart.additionalInfo}
        </p>
      )}
    </Typography>
  );

  return (
    <>
      <Box className={c.minW0}>
        <Tooltip title={t('ALT_CALC_COPY_CODE', 'Click pentru a copia codul')} placement="left">
          <ButtonBase className={c.codeCopy} onClick={copyCode}>
            <Box component="code" className={cx(c.ellipsis, c.semibold)}>
              {code}
            </Box>
            <CustomIcon name="copy" />
          </ButtonBase>
        </Tooltip>
      </Box>
      <Tooltip
        placement="top"
        title={
          alternativePart.remarks ? (
            <div>
              <div
                className={cx(c.semibold, c.dangerText)}
                style={{ fontSize: 16, marginBottom: 8, textAlign: 'center' }}
              >
                {t('CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_REMARKS_LABEL', 'Observatii')}
              </div>
              <div>{alternativePart.remarks}</div>
            </div>
          ) : (
            <div>{alternativePart.name}</div>
          )
        }
      >
        <Typography variant="body2" className={cx(c.ellipsis, c.semibold)}>
          {alternativePart.name}
        </Typography>
      </Tooltip>
      {showNetPrice && (
        <Typography variant="body2" className={cx(c.right, c.tabular, c.semibold)}>
          {alternativePart.alternativeNetPrice == null
            ? DASH
            : displayAmount(alternativePart.alternativeNetPrice, country)}
        </Typography>
      )}
      <Typography variant="body2" className={cx(c.right, c.semibold, c.tabular)}>
        {displayAmount(alternativeUnitPrice(alternativePart), country)}
      </Typography>
      <Box style={{ textAlign: 'center' }}>
        <Box component="span" className={cx(c.badge, c.badgeGeneral)}>
          {alternativeQuality(alternativePart)}
        </Box>
      </Box>
      {alternativePart.deliveryTime ? (
        <Tooltip
          placement="top"
          title={<div>{renderDeliveryTime(alternativePart.deliveryTime)}</div>}
        >
          <Typography variant="body2" className={cx(c.ellipsis, c.semibold)}>
            {renderDeliveryTime(alternativePart.deliveryTime)}
          </Typography>
        </Tooltip>
      ) : (
        <Typography variant="body2" className={cx(c.ellipsis, c.semibold)}>
          {DASH}
        </Typography>
      )}
      <Typography variant="body2" className={cx(c.ellipsis, c.semibold)}>
        {alternativePart.producer}
      </Typography>
      <Typography variant="body2" className={cx(c.ellipsis, c.semibold)}>
        {alternativeSupplierName(alternativePart)}
      </Typography>
      <Typography variant="body2" component="div" className={cx(c.right, c.semibold)}>
        {alternativePart.quantityAdditionalInfo && qty != null ? (
          <Tooltip title={<div>{alternativePart.quantityAdditionalInfo}</div>}>
            <span style={infoValueStyle}>
              <span>{qty}</span>
              <CustomIcon name="info" />
            </span>
          </Tooltip>
        ) : qty == null || isNaN(qty) ? null : supplierIsLkq && qty > 5 ? (
          <span>5+</span>
        ) : (
          <span>{qty}</span>
        )}
      </Typography>
      {variantsEnabled && locationCell}
      {variantsEnabled ? (
        <Typography variant="body2" className={cx(c.right, c.bold, c.tabular, diffClass)}>
          {signedAmount(diff)}
        </Typography>
      ) : (
        locationCell
      )}
    </>
  );
};

export default React.memo(AlternativeCalculationCells);
